
import jsQR from 'jsqr';
import React from "react";
import * as Hooks from "../../../hooks";
import { i18nManager } from "../../../i18n";
import { Capability, Schemes, Utils } from "../../../utils";
import { Elements } from "../../shared/Elements";
import MediaInput from "../../shared/MediaInput";
import * as ReactRouterDom from "react-router-dom";
import { ConfirmDialog, Siren } from "../../shared/ConfirmDialog";
import LoadingGif from "../../../assets/images/loading_circle.gif";
import { ApiResponseData, BaseService, ConsusService, IMember, ITeam, ITeamMember, IUploadedFile, SanitizedResponse, TeamRole, TeamService } from "../../../services";
import { Image, Alignment, Button, ButtonManageRef, Checkbox, Classname, ComposedPassword, DateTimeInput, DOMHelper, FormControl, ObjectHelper, PasswordInput, Popover, PopoverManageRef, Scheme, Subscriber, TextAreaInput, TextInput, useScreenDimension, useSubscription, BoolHelper } from "@rivtn/noseur";
import UpSellPlan from '../../shared/UpSellPlan';

export interface ManageTeamsProps {
    profile: IMember;
}

function ManageTeams(props: ManageTeamsProps) {

    const { profile } = props;
    const { width } = useScreenDimension();
    const labels = i18nManager.Labels.teams;
    useSubscription("TEAM.FETCH", fetchTeams);
    const [themeMode, _] = Hooks.useThemeMode();
    const teamService = TeamService.getInstance();
    const consusService = ConsusService.getInstance();
    const [activeTeam, setActiveTeam] = React.useState<ITeam>();
    const [teams, setTeams] = React.useState<ApiResponseData<ITeam>>({});
    const joinTeamPopOverManageRef = React.useRef<PopoverManageRef>(null);
    const inviteMemberPopOverManageRef = React.useRef<PopoverManageRef>(null);
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const [status, setStatus] = React.useState<"ACTIVE" | "DISBANDED">((searchParams.get("status") as any) ?? "ACTIVE");

    React.useEffect(() => {
        fetchTeams();
    }, [status]);

    return (<div className="teams">
        <div className="header">
            <span className="t">{labels.title}{status === "DISBANDED" ? (<> - <span>{labels.disbanded_teams}</span></>) : null}</span>
            <div className="controls">
                <UpSellPlan capability={Capability.MAX_TEAM_YOU_CAN_JOIN} currentValue={(teams as any).team_join_count} childrenRelayProps={Elements.BUTTON_UPGRADE_PROPS} >
                    <Button raised scheme={Schemes.OMNEHOST} text={labels.join_team} style={{ marginRight: 10 }} onClick={(e: any) => {
                        joinTeamPopOverManageRef.current?.toggle(e);
                    }} />
                </UpSellPlan>
                <UpSellPlan capability={Capability.MAX_TEAM_CREATION} currentValue={(teams as any).team_creation_count} childrenRelayProps={Elements.BUTTON_UPGRADE_PROPS} >
                    <Button raised scheme={Schemes.OMNEHOST} text={labels.create_new_team} onClick={createTeam} />
                </UpSellPlan>
            </div>
        </div>
        <div className="pagination">
            {Elements.buildPaginator(teams ?? {},
                ({ currentPage }, size) => Utils.updateSearchParamses({ "page": currentPage, size }, setSearchParams, fetchTeams),
                parseInt(searchParams.get("page") ?? "1"), { margin: 15, marginBottom: 5 })}
            {(width > 800) && Elements.buildSorter(labels.sorts, (sort) => Utils.updateSearchParamses({ sort }, setSearchParams, fetchTeams), labels.sorts[0].label)}
        </div>
        <div className="query-section">
            <div className="query-inputs">
                {labels.query_inputs.filter((f) => (width > 800 || BoolHelper.equalsAny(f.name, ["name"]))).map((fontControl) => (<TextInput key={fontControl.name}
                    placeholder={fontControl.placeholder} defaultValue={searchParams.get(fontControl.name) ?? undefined}
                    onInputComplete={(v) => Utils.updateSearchParams(fontControl.name, v, setSearchParams, fetchTeams)} />))}
            </div>
            <span className="link" style={{ marginLeft: 10 }} onClick={() => {
                setTeams({});
                const newStatus = (status === "ACTIVE" ? "DISBANDED" : "ACTIVE");
                Utils.updateSearchParams("status", newStatus, setSearchParams, () => setStatus(newStatus));
            }}>{status === "ACTIVE" ? labels.view_disbanded_teams : labels.view_active_teams}</span>
        </div>
        <div className="teams-list">
            {!teams.content && (<div style={{ margin: "90px 110px", display: "flex" }}>
                <img alt={"loading teams"} src={LoadingGif} style={{ width: 50, margin: "auto" }} />
            </div>)}
            {teams.content && !teams.content?.length && (Elements.buildEmptyListView("fas fa-users",
                (status === "DISBANDED" ? (
                    <span>{labels.empty_label_parts.no_disbanded_teams}</span>
                )
                    : (<>
                        <span>{labels.empty_label_parts.you_do_not} <span className="link" onClick={() => {
                            createTeam();
                        }}>{labels.empty_label_parts.create}</span> {i18nManager.Labels.common.or} <span className="link" onClick={(e: any) => {
                            joinTeamPopOverManageRef.current?.toggle(e);
                        }}>{labels.empty_label_parts.join_a_team}</span> {labels.empty_label_parts.to_bring}</span>
                    </>))))}
            {teams.content && !teams.content?.length || true && (teams.content?.map((team) => {
                const teamWebsiteLink = Utils.buildPublicUrl("TEAM", team.public_id);

                return (<div className="team" key={team.external_id}>
                    <Image className="banner" src={team.extra_data?.primary_banner_image} alt={team.name} fallback={i18nManager.Labels.common.default_banner} />
                    <Image className="logo" src={team.logo_url} alt={team.name} fallback={i18nManager.Labels.common.default_logo} />
                    <div>
                        <span className="name text-shadowed">{team.name}</span>
                        <div>
                            <Button linkTarget="_blank" link={teamWebsiteLink} rounded raised borderless scheme={Schemes.OMNEHOST} className="visit" leftIcon="fa fa-computer" />
                            {(team.status === "ACTIVE" && team.is_moderator) && (<>
                                <ReactRouterDom.NavLink className="noseur-badge-overlay" style={{ cursor: "pointer" }} to={`/teams/${team.public_id}/settings`}>
                                    <Button rounded raised borderless scheme={Schemes.OMNEHOST} leftIcon="fa fa-cog" />
                                </ReactRouterDom.NavLink>
                                <Button rounded raised borderless scheme={Schemes.OMNEHOST} leftIcon="fa fa-user-plus" onClick={(e: any) => {
                                    setActiveTeam(team);
                                    inviteMemberPopOverManageRef.current?.show(e);
                                }} />
                            </>)}
                            {(team.status === "ACTIVE" && team.earning_settings?.receiving_member_ids?.includes(profile.external_id)) && (<>
                                <ReactRouterDom.NavLink className="noseur-badge-overlay" style={{ cursor: "pointer" }} to={`/teams/${team.public_id}/analytics`}>
                                    <Button rounded raised borderless scheme={Schemes.OMNEHOST} leftIcon="fa fa-line-chart" />
                                </ReactRouterDom.NavLink>
                            </>)}
                            <ReactRouterDom.NavLink className="noseur-badge-overlay" style={{ cursor: "pointer" }} to={`/messages?team_id=${team.public_id}`}>
                                {(team.un_reads?.messages > 0) && (<span className="noseur-badge noseur-danger noseur-no-border" style={{ bottom: 3, right: 10 }}>{team.un_reads?.messages}</span>)}
                                <Button rounded raised borderless scheme={Schemes.OMNEHOST} leftIcon="far fa-comment" />
                            </ReactRouterDom.NavLink>
                            <ReactRouterDom.NavLink className="noseur-badge-overlay" style={{ cursor: "pointer" }} to={`/teams/${team.public_id}/notifications`}
                                state={{ teamName: team.name }}>
                                {(team.un_reads?.notifications > 0) && (<span className="noseur-badge noseur-danger noseur-no-border" style={{ bottom: 3, right: 10 }}>{team.un_reads?.notifications}</span>)}
                                <Button rounded raised borderless scheme={Schemes.OMNEHOST} leftIcon="far fa-bell" />
                            </ReactRouterDom.NavLink>
                            {(team.status === "ACTIVE" && team.role === TeamRole.OWNER) && (<Button rounded raised borderless scheme={Schemes.OMNEHOST} style={{ padding: "10px 12px" }}
                                leftIcon="exit fa fa-trash" onClick={() => disbandTeam(team)} />)}
                            {(team.status === "ACTIVE" && team.role !== TeamRole.OWNER) && (<Button rounded raised borderless scheme={Schemes.OMNEHOST} style={{ padding: "10px 12px" }}
                                leftIcon="exit fa fa-sign-out" onClick={() => leaveTeam(team)} />)}
                            {(team.status === "DISBANDED" && team.role === TeamRole.OWNER) && (<UpSellPlan capability={Capability.REACTIVATE_DISBANDED_TEAM} childrenRelayProps={{ style: { color: "#FFD700" } }} >
                                <Button rounded raised outlined borderless scheme={Scheme.SUCCESS} style={{ padding: "10px 12px" }}
                                    leftIcon="fa fa-circle" onClick={() => reactivateTeam(team)} />
                            </UpSellPlan>)}
                        </div>
                    </div>
                </div>);
            }))}
        </div>

        <Popover dismissOnClick manageRef={joinTeamPopOverManageRef} className={Classname.build("noseur-popover-b join-team-popover", themeMode)} style={{ marginTop: 55, minWidth: 380 }}>
            <span onClick={joinTeamUsingQrCode}>{labels.popover.join_team.using_qr_code}</span>
            <span onClick={joinTeamUsingJoinCode}>{labels.popover.join_team.using_join_code}</span>
            <span onClick={joinTeamUsingIdAndPasscode}>{labels.popover.join_team.using_id_and_passcode}</span>
        </Popover>
        <Popover dismissOnClick manageRef={inviteMemberPopOverManageRef} className={Classname.build("noseur-popover-b join-team-popover", themeMode)} style={{ marginTop: 55, minWidth: 380 }}>
            <span onClick={shareTeamQrCode}>{labels.popover.invite_member.share_qr_code}</span>
            <span onClick={() => Subscriber.report("TEAM.VIEW_JOIN_CODE", { team: activeTeam })}>{labels.popover.invite_member.share_join_code}</span>
            <span onClick={inviteMember}>{labels.popover.invite_member.send_invite}</span>
        </Popover>
    </div>);

    function fetchTeams() {
        setTeams({});
        teamService.queryTeams({
            status,
            sort: searchParams.get("sort") ?? "",
            name: searchParams.get("name") ?? "",
            page: searchParams.get("page") ?? "1",
            size: searchParams.get("size") ?? "10",
            description: searchParams.get("description") ?? "",
        }).then(({ apiResponse }: SanitizedResponse<ITeam>) => {
            setTeams(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

    function createTeam() {
        const team: Partial<ITeam> = {};
        const dialogLabels = labels.dialog.create_team;
        const dialogErrorRef = { current: null } as any;
        const mediaInputManageRef = { current: undefined as any };
        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmLabel: dialogLabels.create_team,
            style: { minWidth: (width < 800 ? "95%" : 500), maxWidth: "90%" },
            content: (<div style={{ margin: "10px 10px", marginBottom: 30, width: "100%" }}>
                <div style={{ display: "flex", flexDirection: "row", alignItems: "flex-end" }}>
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", marginBottom: 10 }}>
                        <FormControl style={{ marginTop: 15 }} isFieldset fill label={dialogLabels.name}>
                            <TextInput onInputComplete={(v) => {
                                team.name = v;
                                dialogErrorRef.current!.innerText = " ";
                            }} />
                        </FormControl>
                        <ComposedPassword formControlProps={{
                            fill: true,
                            isFieldset: true,
                            style: { marginTop: 15 },
                            label: dialogLabels.passcode,
                        }} onInputComplete={(v) => {
                            (team as any).passcode = v;
                            dialogErrorRef.current!.innerText = " "
                        }} />
                    </div>
                    <MediaInput manageRef={mediaInputManageRef} style={{ alignSelf: "center", marginLeft: 20 }} width={180} height={180}
                        noFillTemplate onSelectFiles={(_) => dialogErrorRef.current!.innerText = " "} onGenerateWithAi={async () => {
                            return new File(["We are here"], "we.txt", { type: 'text/plain' });
                        }} />
                </div>
                <FormControl style={{ marginTop: 15 }} isFieldset fill label={dialogLabels.description}>
                    <TextAreaInput fill style={{ height: 160 }} onInputComplete={(v) => {
                        team.description = v;
                        dialogErrorRef.current!.innerText = " ";
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error"> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                let errorMessage;
                dialogErrorRef.current!.innerText = " ";
                const files = mediaInputManageRef.current?.files() as File[];
                if (!team.name || !team.description) {
                    errorMessage = dialogLabels.name_description_required;
                } else if (!files.length) {
                    errorMessage = dialogLabels.logo_required;
                }
                if (errorMessage) {
                    dialogErrorRef.current!.innerText = errorMessage;
                    return false;
                }
                button?.setLoadingState(true);
                const reportSuccess = () => {
                    button?.setLoadingState(false);
                    Siren.alert(dialogLabels.team_created_successfully);
                    dialog.destroy();
                };
                teamService.createTeam(team).then(({ sanitized: sanitizedTeam, }: SanitizedResponse<ITeam>) => {
                    const file = files[0];
                    consusService.uploadFile(`/omnehost/team/media/logo/`, `${sanitizedTeam.external_id}${ObjectHelper.getExtension(file.name, { lastOnly: true, })}`, file).then(({ sanitized }: SanitizedResponse<IUploadedFile>) => {
                        teamService.updateTeam(sanitizedTeam.external_id, { ...sanitizedTeam, logo_url: sanitized.consus_download_url }).then(() => {
                            fetchTeams();
                        }).catch(() => { }).finally(reportSuccess);
                    }).catch((error: any) => {
                        BaseService.reportError(error);
                        reportSuccess();
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function disbandTeam(team: ITeam) {
        let teamName = "";
        const dialogLabels = labels.dialog.disband_team;
        const dialogErrorRef = { current: null } as any;
        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmScheme: Scheme.DANGER,
            desc: dialogLabels.desc.replace("${team_name}", team.name),
            content: (<div style={{ margin: "10px 0px" }}>
                <p>{dialogLabels.effect_desc} <a href={dialogLabels.article.location} target="_blank">{dialogLabels.article.label}</a></p>
                <p>{labels.dialog.confirm_team.to_continue}</p>
                <FormControl style={{ marginTop: 15 }} isFieldset fill label={labels.dialog.confirm_team.team_name_control.placeholder}>
                    <TextInput onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        teamName = v;
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error"> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                if (teamName !== team.name) {
                    dialogErrorRef.current!.innerText = labels.dialog.confirm_team.name_does_not_match;
                    return false;
                }
                button?.setLoadingState(true);
                teamService.leaveOrDisbandTeam(team.external_id).then(({ apiResponse }: SanitizedResponse<ITeam>) => {
                    dialog.destroy(() => {
                        fetchTeams();
                        Siren.alert(apiResponse.message);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function leaveTeam(team: ITeam) {
        let teamName = "";
        const dialogLabels = labels.dialog.leave_team;
        const dialogErrorRef = { current: null } as any;
        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmScheme: Scheme.DANGER,
            desc: dialogLabels.desc.replace("${team_name}", team.name) + " " + labels.dialog.confirm_team.to_continue,
            content: (<div style={{ margin: "10px 0px" }}>
                <FormControl style={{ marginTop: 15 }} isFieldset fill label={labels.dialog.confirm_team.team_name_control.placeholder}>
                    <TextInput onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        teamName = v;
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error"> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                if (teamName !== team.name) {
                    dialogErrorRef.current!.innerText = labels.dialog.confirm_team.name_does_not_match;
                    return false;
                }
                button?.setLoadingState(true);
                teamService.leaveOrDisbandTeam(team.external_id).then(({ apiResponse }: SanitizedResponse<ITeam>) => {
                    dialog.destroy(() => {
                        fetchTeams();
                        Siren.alert(apiResponse.message);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function reactivateTeam(team: ITeam) {
        let teamName = "";
        const dialogErrorRef = { current: null } as any;
        const dialogLabels = labels.dialog.reactivate_team;

        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmScheme: Scheme.DANGER,
            desc: dialogLabels.desc.replace("${team_name}", team.name),
            content: (<div style={{ margin: "10px 0px" }}>
                <p>{labels.dialog.confirm_team.to_continue}</p>
                <FormControl style={{ marginTop: 15 }} isFieldset fill label={labels.dialog.confirm_team.team_name_control.placeholder}>
                    <TextInput onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        teamName = v;
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error"> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                if (teamName !== team.name) {
                    dialogErrorRef.current!.innerText = labels.dialog.confirm_team.name_does_not_match;
                    return false;
                }
                button?.setLoadingState(true);
                teamService.reactivateTeam(team.external_id).then(({ apiResponse }: SanitizedResponse<ITeam>) => {
                    dialog.destroy(() => {
                        fetchTeams();
                        Siren.alert(apiResponse.message);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function shareTeamQrCode() {
        if (!activeTeam) return;
        const dialogLabels = labels.dialog.share_qr_code;
        const dialog = ConfirmDialog({
            style: { maxWidth: 400 },
            title: dialogLabels.title,
            footerAlignment: Alignment.RIGHT,
            confirmLabel: i18nManager.Labels.common.close,
            desc: dialogLabels.desc.replace("${team_name}", activeTeam.name),
            content: (<div style={{ margin: "10px 0px", display: "flex", flexDirection: "column" }}>
                <img style={{ width: 270, height: 270, margin: "auto" }} src={`data:image/png;base64, ${activeTeam.qr_code_base64}`} alt={activeTeam.name} />
                <span style={{ marginBottom: 10 }}>{labels.dialog.share_to}</span>
                <div className="d-socials" style={{ display: "flex" }}>
                    <i className="fa fa-comment" />
                    <i className="fab fa-x-twitter" />
                    <i className="fab fa-instagram" style={{ color: "#c46b54" }} />
                    <i className="fab fa-whatsapp" style={{ color: "#33b544" }} />
                    <i className="fab fa-facebook" style={{ color: "#2c5ca3" }} />
                </div>
                <a download={activeTeam.name + " QR.png"} style={{ marginTop: 15 }} href={`data:image/png;base64, ${activeTeam.qr_code_base64}`}>{dialogLabels.download_qr_code}</a>
            </div>),
        });
        dialog.show();
    }

    function inviteMember() {
        if (!activeTeam) return;
        const dialogErrorRef = { current: null } as any;
        const dialogLabels = labels.dialog.invite_member;
        const payload = {
            note: null as any as string,
            team_id: activeTeam.external_id,
            expiry_date: null as any as string,
            member_username: null as any as string,
        };
        const dialog = ConfirmDialog({
            closable: true,
            style: { maxWidth: 500 },
            title: dialogLabels.title,
            footerAlignment: Alignment.RIGHT,
            confirmLabel: dialogLabels.send_invite,
            desc: dialogLabels.desc.replace("${team_name}", activeTeam.name),
            content: (<div style={{ margin: "10px 0px", display: "flex", flexDirection: "column" }}>
                <FormControl fill isFieldset label={dialogLabels.member.title} style={{ marginBottom: 15, marginTop: 15 }} scheme={Schemes.OMNEHOST}>
                    <TextInput fill onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        payload.member_username = v;
                    }} />
                </FormControl>
                <DateTimeInput fill scheme={Schemes.OMNEHOST} minDate={new Date()}
                    highlightToday formControlProps={{
                        isFieldset: true,
                        label: dialogLabels.expiry_date.title,
                        rightContent: dialogLabels.expiry_date.icon,
                    }} onSelectDate={(o) => {
                        payload.expiry_date = o.selectedDate!.toUTCString();
                    }} />
                <FormControl fill isFieldset label={dialogLabels.note.title} style={{ marginTop: 15 }} scheme={Schemes.OMNEHOST}>
                    <TextAreaInput fill style={{ height: 150 }} onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        payload.note = v;
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error" style={{ maxWidth: 300 }}> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                teamService.inviteTeamMember(payload).then(({ apiResponse }: SanitizedResponse<any>) => {
                    dialog.destroy(() => {
                        Siren.alert(apiResponse.message);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function joinTeamUsingQrCode() {
        const payload = {
            qrcode_value: null as any as string,
        };
        const dialogErrorRef = { current: null } as any;
        const dialogLabels = labels.dialog.join_with_qr_code;
        const mediaInputManageRef = { current: undefined as any };
        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmLabel: dialogLabels.join_team,
            style: { minWidth: 350, maxWidth: "90%" },
            content: (<div style={{ margin: "10px 0px", display: "flex", flexDirection: "column" }}>
                <MediaInput instruction={dialogLabels.scan_qr_code} width={320} height={320}
                    noControl style={{ alignSelf: "center", zIndex: 1 }} manageRef={mediaInputManageRef}
                    onSelectFiles={async (files) => {
                        const image = await DOMHelper.imageDataFromFileBlock(files[0]);
                        const decodedQR = jsQR(image.imageData.data, image.width, image.height);
                        if (!decodedQR) {
                            dialogErrorRef.current!.innerText = dialogLabels.invalid_qr_code;
                            return;
                        }
                        payload.qrcode_value = decodedQR.data;
                        dialogErrorRef.current!.innerText = " ";
                    }} />
                <span className="link" style={{ margin: 5 }} onClick={() => mediaInputManageRef.current?.select()}>{dialogLabels.upload_team_qr_code}</span>
                <div ref={dialogErrorRef} className="error" style={{ maxWidth: 300 }}> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                teamService.joinTeamWithQrCode(payload).then(({ apiResponse, sanitized }: SanitizedResponse<ITeamMember>) => {
                    dialog.destroy(() => {
                        Siren.alert(apiResponse.message);
                        acceptTeamJoinTerm(sanitized);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function joinTeamUsingJoinCode() {
        const payload = {
            join_code: null as any as string,
        };
        const dialogErrorRef = { current: null } as any;
        const dialogLabels = labels.dialog.join_with_join_code;
        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmLabel: dialogLabels.join_team,
            style: { minWidth: 400, maxWidth: "90%" },
            content: (<div style={{ margin: "10px 0px", display: "flex", flexDirection: "column" }}>
                <FormControl fill isFieldset label={dialogLabels.join_code.title} scheme={Schemes.OMNEHOST}>
                    <TextInput fill onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        payload.join_code = v;
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error" style={{ maxWidth: 300 }}> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                teamService.joinTeamWithJoinCode(payload).then(({ apiResponse, sanitized }: SanitizedResponse<ITeamMember>) => {
                    dialog.destroy(() => {
                        Siren.alert(apiResponse.message);
                        acceptTeamJoinTerm(sanitized);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function joinTeamUsingIdAndPasscode() {
        const payload = {
            team_passcode: null as any as string,
            team_public_id: null as any as string,
        };
        const dialogErrorRef = { current: null } as any;
        const dialogLabels = labels.dialog.join_with_id_and_passcode;
        const dialog = ConfirmDialog({
            closable: true,
            style: { maxWidth: 500 },
            title: dialogLabels.title,
            confirmLabel: dialogLabels.join_team,
            content: (<div style={{ margin: "10px 0px", display: "flex", flexDirection: "column" }}>
                <FormControl fill isFieldset label={dialogLabels.team_id.title} scheme={Schemes.OMNEHOST}>
                    <TextInput fill onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        payload.team_public_id = v;
                    }} />
                </FormControl>
                <FormControl fill isFieldset label={dialogLabels.team_passcode.title} style={{ marginBottom: 15, marginTop: 15 }} scheme={Schemes.OMNEHOST}>
                    <PasswordInput fill onInputComplete={(v: any) => {
                        dialogErrorRef.current!.innerText = " ";
                        payload.team_passcode = v;
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error" style={{ maxWidth: 300 }}> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                teamService.joinTeamWithIdAndPasscode(payload).then(({ apiResponse, sanitized }: SanitizedResponse<ITeamMember>) => {
                    dialog.destroy(() => {
                        Siren.alert(apiResponse.message);
                        acceptTeamJoinTerm(sanitized);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function acceptTeamJoinTerm(teamMember: ITeamMember) {
        const team = teamMember.team;
        const payload = {
            accept_team_rule: null as any as boolean,
            accept_omnehost_team_policy: null as any as boolean,
        };
        const dialogErrorRef = { current: null } as any;
        const dialogLabels = labels.dialog.accept_join_term;
        const dialog = ConfirmDialog({
            title: dialogLabels.title,
            footerAlignment: Alignment.RIGHT,
            confirmLabel: dialogLabels.confirm,
            style: { minWidth: 350, maxWidth: 550 },
            content: (<div style={{ margin: "10px 0px", display: "flex", flexDirection: "column" }}>
                {(!!team.rule) && (<>
                    <span dangerouslySetInnerHTML={{ __html: dialogLabels.desc.replace("${team_name}", team.name) }} />
                    <span style={{ color: "#C6AB1E" }}>{dialogLabels.note}</span>
                    <p style={{ borderRadius: 7, padding: 10, marginTop: 10, marginBottom: 10, height: 300, border: "1px solid #dfdfdf", overflow: "auto" }}
                        dangerouslySetInnerHTML={{ __html: Utils.markdownToHtml(team.rule ?? "") }} />
                </>)}
                <span style={{ color: "#FF7171", fontWeight: "bold" }}>{dialogLabels.disclaimer.replaceAll("${team}", team.name)}</span>
                <Checkbox defaultChecked scheme={Schemes.OMNEHOST} style={{ marginTop: 15 }} label={dialogLabels.accept_team_rule.title} onChecked={(c) => payload.accept_team_rule = c} />
                <Checkbox defaultChecked scheme={Schemes.OMNEHOST} style={{ marginTop: 10 }} onChecked={(c) => payload.accept_omnehost_team_policy = c}
                    label={<span style={{ color: "var(--textColor)" }} dangerouslySetInnerHTML={{ __html: dialogLabels.accept_omnehost_policy.title as string }} />} />
                <div ref={dialogErrorRef} className="error" style={{ maxWidth: 300 }}> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                teamService.acceptTeamRules(teamMember.external_id, payload).then(({ apiResponse }: SanitizedResponse<any>) => {
                    dialog.destroy(() => {
                        Siren.alert(apiResponse.message);
                        fetchTeams();
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

}

export default ManageTeams;
